import { isEmpty, isNil } from 'lodash';
import { useState } from 'react';
import { Icon } from '@shared/components/Icon';
import { Menu, Submenu } from '@shared/components/Menu';
import { View } from '@shared/components/View';
import { LoadingIndicator } from '@shared/components/loading';
import { MBSearchBar } from '@shared/components/searchBar';
import { Colors } from '@shared/styles';
import { API } from '@shared/types/api';
import { i18n } from '@shared/util/i18n';
import { TagMultiSelectOptions } from './TagMultiSelectOptions';
import { TagOptionPill } from './TagOptionPill';
import { TagSingleSelectOptions } from './TagSingleSelectOptions';
import styles from './TagEditFieldMenu.module.css';

/**
 * TagEditFieldMenu
 *
 * The picker `TagEditField` opens from its summary. One menu for both single and multi select:
 * the option list differs (`TagSingleSelectOptions` / `TagMultiSelectOptions`), while the title
 * bar, create, and the manage → edit steps are shared.
 *
 * - Create (`onCreate`) is an item at the bottom that opens a `Menu.NameForm` submenu, prefilled
 *   with the search text or a placeholder name.
 * - With `isEditable`, a gear steps to a manage view listing every tag (archived ones in their own
 *   section), and from there to an edit view to rename, archive or unarchive one.
 */

type TagOption = API.TagComponentOption;

type TagMenuView = { name: 'select' } | { name: 'manage' } | { name: 'edit'; value: string };

interface TagEditFieldMenuProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  anchorName: string;
  label: string;
  /** Every option the component has, archived ones included. */
  options: TagOption[];
  selectedValues: string[];
  isMultiselect: boolean;
  onChange: (values: string[]) => void;
  onCreate?: (label: string) => Promise<void>;
  isEditable: boolean;
  onRename: (value: string, label: string) => Promise<void>;
  onArchive: (value: string) => Promise<void>;
  onUnarchive: (value: string) => Promise<void>;
}

export function TagEditFieldMenu({
  isOpen,
  onOpenChange,
  anchorName,
  ...contentProps
}: TagEditFieldMenuProps) {
  return (
    // Every Menu.Item closes the menu when closeOnSelect is on: multi-select
    // rows, manage rows and the archive item all need it to stay open. The
    // single-select list closes the menu itself.
    <Menu isOpen={isOpen} onOpenChange={onOpenChange} anchorName={anchorName} closeOnSelect={false}>
      <Menu.Content
        mobileSheet
        editFieldMenu
        className={styles.tagEditFieldMenu}
        aria-label={contentProps.label}
      >
        <TagMenuContent {...contentProps} />
      </Menu.Content>
    </Menu>
  );
}

/**
 * Everything inside the open menu. `Menu.Content` renders nothing while the menu is closed, so the
 * view and search state here start over on each open.
 */
function TagMenuContent({
  label,
  options,
  selectedValues,
  isMultiselect,
  onChange,
  onCreate,
  isEditable,
  onRename,
  onArchive,
  onUnarchive,
}: Omit<TagEditFieldMenuProps, 'isOpen' | 'onOpenChange' | 'anchorName'>) {
  const [view, setView] = useState<TagMenuView>({ name: 'select' });
  // Shared by the select and manage views, as the old sheet's single search box was
  const [filter, setFilter] = useState('');

  // Rename and archive return to the manage list, as the old sheet did
  const backToManage = () => setView({ name: 'manage' });

  switch (view.name) {
    case 'select':
      return (
        <TagSelectView
          label={label}
          options={options}
          selectedValues={selectedValues}
          isMultiselect={isMultiselect}
          onChange={onChange}
          onCreate={onCreate}
          isEditable={isEditable}
          filter={filter}
          setFilter={setFilter}
          setView={setView}
        />
      );
    case 'manage':
      return (
        <TagManageView options={options} filter={filter} setFilter={setFilter} setView={setView} />
      );
    case 'edit':
      return (
        <EditTagView
          key={view.value}
          options={options}
          value={view.value}
          onBack={backToManage}
          onRename={onRename}
          onArchive={onArchive}
          onUnarchive={onUnarchive}
        />
      );
  }
}

function TagSelectView({
  label,
  options,
  selectedValues,
  isMultiselect,
  onChange,
  onCreate,
  isEditable,
  filter,
  setFilter,
  setView,
}: Pick<
  TagEditFieldMenuProps,
  'label' | 'options' | 'selectedValues' | 'isMultiselect' | 'onChange' | 'onCreate' | 'isEditable'
> & {
  filter: string;
  setFilter: (filter: string) => void;
  setView: (view: TagMenuView) => void;
}) {
  const activeOptions = options.filter(option => !option.archived);
  const optionsProps = {
    options: activeOptions,
    selectedValues,
    onChange,
    filter,
    setFilter,
    canCreate: !isNil(onCreate),
  };

  return (
    <>
      <Menu.TitleBar
        title={label}
        mobileOnly={!isEditable}
        endContent={
          <>
            {isEditable && (
              <Menu.GearButton
                label={i18n('multiSelect.manage', { item: getItemLabelPlural() })}
                onClick={() => setView({ name: 'manage' })}
              />
            )}
            <Menu.CloseButton className={styles.mobileOnly} />
          </>
        }
      />
      <View flex>
        {isMultiselect && <TagMultiSelectOptions {...optionsProps} />}
        {!isMultiselect && <TagSingleSelectOptions {...optionsProps} />}
      </View>
      {!isNil(onCreate) && (
        <CreateTag
          query={filter.trim()}
          options={options}
          onCreate={async name => {
            await onCreate(name);
            setFilter('');
          }}
        />
      )}
    </>
  );
}

/** Every tag, archived ones in their own section, each opening the edit view. */
function TagManageView({
  options,
  filter,
  setFilter,
  setView,
}: {
  options: TagOption[];
  filter: string;
  setFilter: (filter: string) => void;
  setView: (view: TagMenuView) => void;
}) {
  const query = filter.toLowerCase();
  const matches = options.filter(option => (option.label ?? '').toLowerCase().includes(query));
  const activeMatches = matches.filter(option => !option.archived);
  const archivedMatches = matches.filter(option => option.archived);

  // The list remounted after an edit in the old sheet, so its search started over
  const onEdit = (option: TagOption) => {
    setFilter('');
    setView({ name: 'edit', value: option.value });
  };

  return (
    <>
      <Menu.TitleBar
        title={i18n('multiSelect.manage', { item: getItemLabelPlural() })}
        startContent={<Menu.BackButton onClick={() => setView({ name: 'select' })} />}
      />
      <Menu.Section>
        <MBSearchBar value={filter} onChangeText={setFilter} />
      </Menu.Section>
      <Menu.Scrollable showScrollIndicators>
        {activeMatches.map(option => (
          <ManageTagItem key={option.value} option={option} onClick={onEdit} />
        ))}
        {!isEmpty(archivedMatches) && (
          <>
            <Menu.Separator />
            <Menu.Section title={i18n('common.archived')} />
          </>
        )}
        {archivedMatches.map(option => (
          <ManageTagItem key={option.value} option={option} onClick={onEdit} />
        ))}
      </Menu.Scrollable>
    </>
  );
}

/**
 * "Create Tag “<query>”" (or "Create Tag" with an empty search) opens a form prefilled with
 * the search text or a placeholder name. A name that matches an existing tag can't be saved, and
 * the form says why.
 */
function CreateTag({
  query,
  options,
  onCreate,
}: {
  query: string;
  options: TagOption[];
  onCreate: (name: string) => Promise<void>;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState('');
  const trimmed = name.trim();
  const exists = tagNameExists(options, trimmed);
  const label = i18n('card.blocks.tagsInput.createTagLabel');
  const itemLabel = isEmpty(query) ? label : `${label} “${query}”`;

  const create = async (value: string) => {
    await onCreate(value.trim());
    setIsOpen(false);
  };

  return (
    <>
      <Menu.Separator />
      <Submenu isOpen={isOpen} onOpenChange={setIsOpen}>
        <Submenu.Trigger>
          <Menu.Item
            icon="plus"
            aria-label={itemLabel}
            onClick={() =>
              setName(isEmpty(query) ? i18n('card.blocks.tagsInput.newTagName') : query)
            }
          >
            <Menu.CreateLabel label={label} query={query} />
          </Menu.Item>
        </Submenu.Trigger>
        <Submenu.Content mobileSheet>
          <Menu.TitleBar endContent={<Menu.CloseButton />} mobileOnly />
          <Menu.NameForm
            label={label}
            value={name}
            setValue={setName}
            buttonLabel={i18n('common.create')}
            canSave={!isEmpty(trimmed) && !exists}
            error={getTagExistsError(exists)}
            onSave={create}
          />
        </Submenu.Content>
      </Submenu>
    </>
  );
}

/**
 * Rename, archive or unarchive one tag, then return to the manage list. Renders nothing if the tag is
 * no longer among the options.
 */
function EditTagView({
  options,
  value,
  onBack,
  onRename,
  onArchive,
  onUnarchive,
}: Pick<TagEditFieldMenuProps, 'options' | 'onRename' | 'onArchive' | 'onUnarchive'> & {
  value: string;
  /** The back button, and where a rename or archive goes when it finishes. */
  onBack: () => void;
}) {
  const option = options.find(item => item.value === value);
  const [name, setName] = useState(option?.label ?? '');
  const [isArchiving, setIsArchiving] = useState(false);

  if (isNil(option)) {
    return null;
  }

  const exists = tagNameExists(
    options.filter(item => item.value !== option.value),
    name.trim(),
  );

  const rename = async (newName: string) => {
    await onRename(option.value, newName.trim());
    onBack();
  };

  const toggleArchived = async () => {
    setIsArchiving(true);
    try {
      await (option.archived ? onUnarchive(option.value) : onArchive(option.value));
      onBack();
    } finally {
      setIsArchiving(false);
    }
  };

  return (
    <>
      <Menu.TitleBar
        title={i18n('multiSelect.edit', { item: i18n('card.blocks.tagsInput.itemLabel') })}
        startContent={<Menu.BackButton onClick={onBack} />}
      />
      <Menu.NameForm
        label={i18n('multiSelect.rename', { item: i18n('card.blocks.tagsInput.itemLabel') })}
        value={name}
        setValue={setName}
        buttonLabel={i18n('common.rename')}
        canSave={!isEmpty(name.trim()) && name !== option.label && !exists}
        error={getTagExistsError(exists)}
        onSave={rename}
      />
      <Menu.Separator />
      <Menu.Item
        icon="archive-box"
        danger={!option.archived}
        label={option.archived ? i18n('common.unarchive') : i18n('common.archive')}
        disabled={isArchiving}
        endContent={isArchiving && <LoadingIndicator className={styles.archiveSpinner} />}
        onClick={() => void toggleArchived()}
      />
    </>
  );
}

function ManageTagItem({
  option,
  onClick,
}: {
  option: TagOption;
  onClick: (option: TagOption) => void;
}) {
  return (
    <Menu.Item
      onClick={() => onClick(option)}
      endContent={<Icon name="arrow-point-right" color={Colors.v2.gray30} gridSize={3} />}
    >
      <View flex marginLeft={-1} paddingVertical={0.5}>
        <TagOptionPill option={option} />
      </View>
    </Menu.Item>
  );
}

/** Tag names are unique regardless of case and surrounding spaces. */
function tagNameExists(options: TagOption[], name: string) {
  return options.some(option => option.label?.trim().toLowerCase() === name.toLowerCase());
}

function getTagExistsError(exists: boolean) {
  return exists ? i18n('card.blocks.tagsInput.tagExistsError') : undefined;
}

function getItemLabelPlural() {
  return i18n('card.blocks.tagsInput.itemLabelPlural');
}
